import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, setDoc, updateDoc } from "firebase/firestore";
import { deleteObject, ref as storageRef } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import type { Issue, IssueStatus, Notice, NoticeType, Severity } from "../types";

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export async function createClub(data: {
  name: string;
  phone: string;
  email: string;
  address: string;
  website?: string;
  greenFee?: string;
  platzreife?: string;
  turnier?: string;
  pricesUrl?: string;
  eventsUrl?: string;
  teeTimeUrl?: string;
  restaurantHours?: string;
  sekretariatHours?: string;
  latitude?: number;
  longitude?: number;
}): Promise<string> {
  const clubId = slugify(data.name) || `club-${Date.now()}`;
  const payload = stripUndefined({
    name: data.name,
    contact: stripUndefined({
      phone: data.phone,
      email: data.email,
      address: data.address,
      website: data.website || undefined,
    }),
    location: {
      latitude: data.latitude ?? 0,
      longitude: data.longitude ?? 0,
    },
    prices: stripUndefined({
      greenFee: data.greenFee || undefined,
      platzreife: data.platzreife || undefined,
      turnier: data.turnier || undefined,
    }),
    pricesUrl: data.pricesUrl || undefined,
    eventsUrl: data.eventsUrl || undefined,
    teeTimeUrl: data.teeTimeUrl || undefined,
    openingHours: stripUndefined({
      restaurant: data.restaurantHours || undefined,
      sekretariat: data.sekretariatHours || undefined,
    }),
  });
  await setDoc(doc(db, "clubs", clubId), payload);
  return clubId;
}

export async function updateClub(
  clubId: string,
  data: {
    name: string;
    phone: string;
    email: string;
    address: string;
    website?: string;
    greenFee?: string;
    platzreife?: string;
    turnier?: string;
    pricesUrl?: string;
    eventsUrl?: string;
    teeTimeUrl?: string;
    restaurantHours?: string;
    sekretariatHours?: string;
    latitude?: number;
    longitude?: number;
  }
) {
  const payload = stripUndefined({
    name: data.name,
    contact: stripUndefined({
      phone: data.phone,
      email: data.email,
      address: data.address,
      website: data.website || undefined,
    }),
    location:
      data.latitude !== undefined && data.longitude !== undefined
        ? { latitude: data.latitude, longitude: data.longitude }
        : undefined,
    prices: stripUndefined({
      greenFee: data.greenFee || undefined,
      platzreife: data.platzreife || undefined,
      turnier: data.turnier || undefined,
    }),
    pricesUrl: data.pricesUrl || undefined,
    eventsUrl: data.eventsUrl || undefined,
    teeTimeUrl: data.teeTimeUrl || undefined,
    openingHours: stripUndefined({
      restaurant: data.restaurantHours || undefined,
      sekretariat: data.sekretariatHours || undefined,
    }),
  });
  await setDoc(doc(db, "clubs", clubId), payload, { merge: true });
}

export async function deleteClub(clubId: string) {
  for (const sub of ["course_info", "events", "issues", "members"]) {
    const snap = await getDocs(collection(db, "clubs", clubId, sub));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  }
  await deleteDoc(doc(db, "clubs", clubId));
}

export async function addNotice(
  clubId: string,
  data: { title: string; text: string; severity: Severity; type: NoticeType }
) {
  const payload: Omit<Notice, "id"> = {
    ...data,
    active: true,
    createdAt: Date.now(),
  };
  await addDoc(collection(db, "clubs", clubId, "course_info"), payload);
}

export async function updateNotice(
  clubId: string,
  noticeId: string,
  data: { title: string; text: string; severity: Severity }
) {
  await updateDoc(doc(db, "clubs", clubId, "course_info", noticeId), { ...data });
}

export async function deleteNotice(clubId: string, noticeId: string) {
  await deleteDoc(doc(db, "clubs", clubId, "course_info", noticeId));
}

export async function listNotices(clubId: string): Promise<Notice[]> {
  const q = query(collection(db, "clubs", clubId, "course_info"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Notice, "id">) }));
}

export async function addEvent(
  clubId: string,
  data: { title: string; date: number; description: string; registrationUrl?: string }
) {
  const payload = stripUndefined({
    title: data.title,
    date: data.date,
    description: data.description,
    registrationUrl: data.registrationUrl || undefined,
    active: true,
  });
  await addDoc(collection(db, "clubs", clubId, "events"), payload);
}

export async function updateEvent(
  clubId: string,
  eventId: string,
  data: { title: string; date: number; description: string; registrationUrl?: string }
) {
  const payload = stripUndefined({
    title: data.title,
    date: data.date,
    description: data.description,
    registrationUrl: data.registrationUrl || undefined,
  });
  await updateDoc(doc(db, "clubs", clubId, "events", eventId), payload);
}

export async function deleteEvent(clubId: string, eventId: string) {
  await deleteDoc(doc(db, "clubs", clubId, "events", eventId));
}

export async function listEvents(clubId: string) {
  const q = query(collection(db, "clubs", clubId, "events"), orderBy("date", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) })) as Array<{
    id: string;
    title: string;
    date: number;
    description: string;
    registrationUrl?: string;
    active: boolean;
  }>;
}

export async function listIssues(clubId: string): Promise<Issue[]> {
  const q = query(collection(db, "clubs", clubId, "issues"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Issue, "id">) }));
}

export async function updateIssueStatus(clubId: string, issueId: string, status: IssueStatus) {
  await updateDoc(doc(db, "clubs", clubId, "issues", issueId), { status });
}

export async function deleteIssue(clubId: string, issueId: string, photoURLs: string[]) {
  await Promise.all(
    photoURLs.map(async (url) => {
      try {
        await deleteObject(storageRef(storage, url));
      } catch {
        // Foto war schon weg oder Storage-Regeln haben's blockiert — Firestore-Eintrag trotzdem löschen.
      }
    })
  );
  await deleteDoc(doc(db, "clubs", clubId, "issues", issueId));
}
